/**
 * Produtos e Serviços — regras puras de cadastro, importação e exportação
 * (.xlsx), portadas da DEMO-03 (frontend/prototipo/src/importacaoProdutos.ts)
 * e usadas no servidor (que decide) e nas telas (que mostram):
 *  - as MESMAS validações do cadastro manual valem na importação (`validarProduto`);
 *  - PN/SKU obrigatório para todos os tipos, exceto Serviço;
 *  - identificação: fabricante + PN normalizados; sem PN (serviço), fabricante + descrição;
 *  - prévia antes de gravar: novos, possíveis atualizações (antes → depois), possíveis
 *    duplicados, sem alteração e erros (com a linha). Nenhuma sobrescrita silenciosa:
 *    atualizações e possíveis duplicados só entram com seleção explícita.
 * Tipos do item (DE/PARA D6, aprovado): Produto, Acessório, Licença, Serviço, Garantia.
 * Pilares: lista parametrizada (P-03), recebida por quem chama.
 */
import { gerarXlsx } from './planilha.ts';

export const TIPOS_ITEM = ['Produto', 'Serviço', 'Licença', 'Garantia', 'Acessório'] as const;
export type TipoItem = (typeof TIPOS_ITEM)[number];
export type Moeda = 'BRL' | 'USD';
export const MOEDAS: Moeda[] = ['BRL', 'USD'];
const SITUACOES = ['Ativo', 'Inativo'] as const;

export interface Produto {
  id: string;
  sku: string; // PN/SKU
  nome: string; // descrição
  fabricante: string;
  categoria: string;
  pilar: string;
  tipoItem: TipoItem;
  unidade: string;
  moeda: Moeda;
  situacao: 'Ativo' | 'Inativo';
  observacoes?: string;
}

export const VERSAO_MODELO_PRODUTOS = 'PRODUTOS-SERVICOS v1';
export const COLUNAS_PRODUTOS = ['Tipo', 'PN/SKU', 'Descrição', 'Fabricante', 'Categoria', 'Pilar', 'Unidade', 'Moeda', 'Situação', 'Observações'] as const;
/** Limites técnicos da importação (arquivo e linhas). */
export const LIMITES_IMPORTACAO = { bytes: 700 * 1024, linhas: 5000 };

/** PN/SKU é obrigatório para todos os tipos, exceto Serviço. */
export const exigePn = (tipo: TipoItem) => tipo !== 'Serviço';
const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
/** Normalização para detectar duplicidade (fabricante + PN). */
export const chaveProduto = (fabricante: string, sku: string) => `${norm(fabricante)}|${sku.toUpperCase().replace(/[^A-Z0-9]/g, '')}`;
/** Chave de identificação no catálogo (fabricante + PN; serviço sem PN: fabricante + descrição). */
export const chaveCatalogo = (p: Pick<Produto, 'fabricante' | 'sku' | 'nome'>) => (p.sku.trim() ? chaveProduto(p.fabricante, p.sku) : `sem-pn|${norm(p.fabricante)}|${norm(p.nome)}`);

/** Validação ÚNICA (cadastro manual e importação). Mensagens exatas do cadastro manual. */
export function validarProduto(p: Produto, pilares: readonly string[]): string[] {
  return [
    !TIPOS_ITEM.includes(p.tipoItem) && `Tipo inválido: “${p.tipoItem}” (use ${TIPOS_ITEM.join(', ')}).`,
    exigePn(p.tipoItem) && !p.sku.trim() && 'Informe o PN/SKU.',
    !p.nome.trim() && 'Informe a descrição.',
    !p.fabricante.trim() && 'Informe o fabricante.',
    !p.categoria.trim() && 'Informe a categoria.',
    !p.pilar ? 'Selecione o pilar.' : !pilares.includes(p.pilar) && `Pilar inválido: “${p.pilar}” (use ${pilares.join(', ')}).`,
    !MOEDAS.includes(p.moeda) && `Moeda inválida: “${p.moeda}” (use BRL ou USD).`,
    !SITUACOES.includes(p.situacao) && `Situação inválida: “${p.situacao}” (use Ativo ou Inativo).`,
  ].filter(Boolean) as string[];
}
/** Produto já cadastrado com a mesma identificação (para bloquear duplicidade). */
export const duplicadoNoCatalogo = (catalogo: Produto[], p: Produto) => catalogo.find((x) => x.id !== p.id && chaveCatalogo(x) === chaveCatalogo(p));

/* ---------------- Modelo e exportação ---------------- */
const linhaDe = (p: Produto): string[] => [p.tipoItem, p.sku, p.nome, p.fabricante, p.categoria, p.pilar, p.unidade, p.moeda, p.situacao, p.observacoes ?? ''];
export const gerarModeloProdutos = (pilares: readonly string[]) =>
  gerarXlsx(
    [
      [VERSAO_MODELO_PRODUTOS, 'Preencha a partir da linha 3. Obrigatórios: Tipo, Descrição, Fabricante, Categoria, Pilar (PN/SKU obrigatório exceto para Serviço). Moeda: BRL ou USD. Situação: Ativo ou Inativo.'],
      [...COLUNAS_PRODUTOS],
      ['Produto', 'PN-EXEMPLO-01', 'Produto de exemplo (fictício)', 'Fabricante Exemplo', 'Categoria Exemplo', pilares[0] ?? '', 'UN', 'BRL', 'Ativo', 'Linha de exemplo: apague antes de importar'],
      ['Serviço', '', 'Serviço de exemplo sem PN (fictício)', 'INTTRIX (serviço)', 'Instalação', pilares[0] ?? '', 'UN', 'BRL', 'Ativo', 'Serviço pode ficar sem PN/SKU'],
    ],
    'Produtos',
  );
/** Exportação do catálogo atual, no MESMO formato do modelo (pode ser reimportada). */
export const exportarCatalogo = (catalogo: Produto[]) => gerarXlsx([[VERSAO_MODELO_PRODUTOS, `Exportado do INTTRIX HUB — ${catalogo.length} item(ns)`], [...COLUNAS_PRODUTOS], ...catalogo.map(linhaDe)], 'Produtos');

/* ---------------- Prévia da importação ---------------- */
export type SituacaoImportacao = 'novo' | 'atualizacao' | 'possivel_duplicado' | 'sem_alteracao' | 'erro';
export interface Diferenca {
  campo: string;
  antes: string;
  depois: string;
}
export interface LinhaImportacao {
  linha: number; // número da linha na planilha
  situacao: SituacaoImportacao;
  produto: Produto;
  erros: string[];
  existente?: Produto;
  diferencas?: Diferenca[];
  motivo?: string;
}
export interface PreviaImportacao {
  erroArquivo?: string;
  linhas: LinhaImportacao[];
}
const CAMPOS_COMPARADOS: [keyof Produto, string][] = [
  ['sku', 'PN/SKU'],
  ['nome', 'Descrição'],
  ['fabricante', 'Fabricante'],
  ['categoria', 'Categoria'],
  ['pilar', 'Pilar'],
  ['tipoItem', 'Tipo'],
  ['unidade', 'Unidade'],
  ['moeda', 'Moeda'],
  ['situacao', 'Situação'],
  ['observacoes', 'Observações'],
];

export function analisarImportacao(linhas: string[][], catalogo: Produto[], pilares: readonly string[], novoId: (n: number) => string): PreviaImportacao {
  if ((linhas[0]?.[0] ?? '').trim() !== VERSAO_MODELO_PRODUTOS) return { erroArquivo: `Arquivo fora do modelo: a célula A1 deve conter “${VERSAO_MODELO_PRODUTOS}”. Baixe o modelo e preencha a partir da linha 3.`, linhas: [] };
  const cab = (linhas[1] ?? []).map((x) => x.trim());
  const faltando = COLUNAS_PRODUTOS.filter((c) => !cab.includes(c));
  if (faltando.length) return { erroArquivo: `Cabeçalho incompleto na linha 2: faltam ${faltando.join(', ')}.`, linhas: [] };
  if (linhas.length - 2 > LIMITES_IMPORTACAO.linhas) return { erroArquivo: `A planilha tem ${linhas.length - 2} linhas; o limite por importação é ${LIMITES_IMPORTACAO.linhas}. Divida o arquivo.`, linhas: [] };
  const col = (l: string[], c: (typeof COLUNAS_PRODUTOS)[number]) => (l[cab.indexOf(c)] ?? '').trim();
  const vistos = new Map<string, number>();
  const saida: LinhaImportacao[] = [];
  linhas.slice(2).forEach((l, i) => {
    const numero = i + 3;
    if (l.every((x) => !String(x ?? '').trim())) return; // linha vazia: ignorada sem erro
    const produto: Produto = {
      id: novoId(numero),
      tipoItem: (col(l, 'Tipo') || 'Produto') as TipoItem,
      sku: col(l, 'PN/SKU').toUpperCase(),
      nome: col(l, 'Descrição'),
      fabricante: col(l, 'Fabricante'),
      categoria: col(l, 'Categoria'),
      pilar: col(l, 'Pilar'),
      unidade: col(l, 'Unidade') || 'UN',
      moeda: (col(l, 'Moeda').toUpperCase() || 'BRL') as Moeda,
      situacao: (col(l, 'Situação') || 'Ativo') as Produto['situacao'],
      observacoes: col(l, 'Observações'),
    };
    const erros = validarProduto(produto, pilares);
    if (erros.length) return saida.push({ linha: numero, situacao: 'erro', produto, erros });
    const chave = chaveCatalogo(produto);
    if (vistos.has(chave)) return saida.push({ linha: numero, situacao: 'possivel_duplicado', produto, erros: [], motivo: `repete a linha ${vistos.get(chave)} deste arquivo` });
    vistos.set(chave, numero);
    const existente = catalogo.find((x) => chaveCatalogo(x) === chave);
    if (existente) {
      const diferencas = CAMPOS_COMPARADOS.map(([k, rotulo]) => ({ campo: rotulo, antes: String(existente[k] ?? ''), depois: String(produto[k] ?? '') })).filter((x) => x.antes !== x.depois);
      return saida.push({ linha: numero, situacao: diferencas.length ? 'atualizacao' : 'sem_alteracao', produto: { ...produto, id: existente.id }, erros: [], existente, diferencas });
    }
    // Mesma descrição e fabricante de um item existente, mas com outro PN: possível duplicado (não entra sem confirmação).
    const parecido = catalogo.find((x) => norm(x.nome) === norm(produto.nome) && norm(x.fabricante) === norm(produto.fabricante));
    if (parecido) return saida.push({ linha: numero, situacao: 'possivel_duplicado', produto, erros: [], existente: parecido, motivo: `mesma descrição e fabricante de ${parecido.sku || 'item sem PN'} já cadastrado` });
    saida.push({ linha: numero, situacao: 'novo', produto, erros: [] });
  });
  return { linhas: saida };
}

/** Seleção padrão: novos marcados; atualizações e possíveis duplicados DESMARCADOS (só com seleção explícita). */
export const selecaoPadrao = (p: PreviaImportacao) => new Set(p.linhas.filter((l) => l.situacao === 'novo').map((l) => l.linha));
/** Linhas que podem ser escolhidas na prévia. */
export const selecionavel = (l: Pick<LinhaImportacao, 'situacao' | 'motivo'>) => (l.situacao === 'novo' || l.situacao === 'atualizacao' || l.situacao === 'possivel_duplicado') && !l.motivo?.startsWith('repete');

export interface RelatorioImportacao {
  incluidos: number;
  atualizados: number;
  ignorados: number;
  comErro: number;
}
/** O que será gravado e o relatório final (incluídos, atualizados, ignorados, com erro). */
export function resultadoImportacao(p: PreviaImportacao, selecionadas: Set<number>): { incluir: Produto[]; atualizar: Produto[]; relatorio: RelatorioImportacao } {
  const sel = (l: LinhaImportacao) => selecionadas.has(l.linha);
  const incluir = p.linhas.filter((l) => (l.situacao === 'novo' || l.situacao === 'possivel_duplicado') && sel(l) && !l.motivo?.startsWith('repete')).map((l) => l.produto);
  const atualizar = p.linhas.filter((l) => l.situacao === 'atualizacao' && sel(l)).map((l) => l.produto);
  const comErro = p.linhas.filter((l) => l.situacao === 'erro').length;
  return { incluir, atualizar, relatorio: { incluidos: incluir.length, atualizados: atualizar.length, ignorados: p.linhas.length - comErro - incluir.length - atualizar.length, comErro } };
}
export const textoRelatorio = (r: RelatorioImportacao) => `${r.incluidos} incluído(s), ${r.atualizados} atualizado(s), ${r.ignorados} ignorado(s), ${r.comErro} com erro`;
